import { useState } from 'react';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import { Layout } from '@/components/layout/Layout';
import { BotaoFlutuante } from '@/components/layout/BotaoFlutuante';
import { Plus, X, RefreshCw, Pencil, Star, Layers, AlertTriangle } from 'lucide-react';
import { usePlans, useCreatePlan, useUpdatePlan } from '@/hooks/useSubscription';
import { TableSkeleton } from '@/components/common/Skeletons';
import { Button } from '@/components/ui/button';
import { Selo } from '@/components/common/ListaMobile';
import { cn } from '@/lib/utils';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import type { Plano, PlanoInput } from '@/types/subscription.types';

type FormState = {
  codigo: string;
  nome: string;
  descricao_escopo: string;
  preco_mensal_centavos: number;
  preco_anual_centavos: number;
  store_product_mensal_ios: string;
  store_product_anual_ios: string;
  store_product_mensal_android: string;
  store_product_anual_android: string;
  limite_animais: number;
  limite_funcionarios: number;
  limite_propriedades: number;
  features: string;
  popular: boolean;
  ativo: boolean;
  ordem: number;
};

const emptyForm: FormState = {
  codigo: '',
  nome: '',
  descricao_escopo: '',
  preco_mensal_centavos: 0,
  preco_anual_centavos: 0,
  store_product_mensal_ios: '',
  store_product_anual_ios: '',
  store_product_mensal_android: '',
  store_product_anual_android: '',
  limite_animais: 0,
  limite_funcionarios: 0,
  limite_propriedades: 0,
  features: '',
  popular: false,
  ativo: true,
  ordem: 0,
};

function planToForm(plano: Plano): FormState {
  return {
    codigo: plano.codigo,
    nome: plano.nome,
    descricao_escopo: plano.descricao_escopo ?? '',
    preco_mensal_centavos: plano.preco_mensal_centavos ?? 0,
    preco_anual_centavos: plano.preco_anual_centavos ?? 0,
    store_product_mensal_ios: plano.store_product_mensal_ios ?? '',
    store_product_anual_ios: plano.store_product_anual_ios ?? '',
    store_product_mensal_android: plano.store_product_mensal_android ?? '',
    store_product_anual_android: plano.store_product_anual_android ?? '',
    limite_animais: plano.limite_animais,
    limite_funcionarios: plano.limite_funcionarios,
    limite_propriedades: plano.limite_propriedades,
    features: (plano.features ?? []).join('\n'),
    popular: plano.popular,
    ativo: plano.ativo,
    ordem: plano.ordem,
  };
}

// Monta o payload da API a partir do form. No create, `codigo` é obrigatório;
// no update enviamos o conjunto completo (a API aceita parcial via .min(1)).
function formToInput(form: FormState): PlanoInput {
  return {
    codigo: form.codigo.trim(),
    nome: form.nome.trim(),
    descricao_escopo: form.descricao_escopo.trim() || null,
    preco_mensal_centavos: form.preco_mensal_centavos || null,
    preco_anual_centavos: form.preco_anual_centavos || null,
    store_product_mensal_ios: form.store_product_mensal_ios.trim() || null,
    store_product_anual_ios: form.store_product_anual_ios.trim() || null,
    store_product_mensal_android: form.store_product_mensal_android.trim() || null,
    store_product_anual_android: form.store_product_anual_android.trim() || null,
    limite_animais: form.limite_animais,
    limite_funcionarios: form.limite_funcionarios,
    limite_propriedades: form.limite_propriedades,
    features: form.features
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean),
    popular: form.popular,
    ativo: form.ativo,
    ordem: form.ordem,
  };
}

// Cartão do plano no celular/tablet (desenho aprovado "Planos"): nome, selos,
// código, preço mensal em destaque com o anual ao lado e uma faixa com os três
// limites. O cartão inteiro abre a mesma edição do botão Editar da tabela.
function PlanoCard({ plano, onEdit }: { plano: Plano; onEdit: () => void }) {
  const limites = [
    { valor: plano.limite_animais, rotulo: 'animais' },
    { valor: plano.limite_funcionarios, rotulo: 'funcionários' },
    { valor: plano.limite_propriedades, rotulo: 'propriedades' },
  ];
  return (
    <button
      type="button"
      onClick={onEdit}
      aria-label={`Editar plano ${plano.nome}`}
      className={cn(
        'flex w-full flex-col gap-3 rounded-2xl border bg-card p-4 text-left shadow-card transition-colors active:bg-muted/60',
        plano.popular ? 'border-tertiary/40' : 'border-border'
      )}
    >
      <span className="flex w-full items-start gap-2.5">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[17px] font-bold text-text-title">{plano.nome}</span>
            {plano.popular && <Selo tom="destaque">Popular</Selo>}
          </span>
          <span className="truncate font-mono text-xs text-text-hint">{plano.codigo}</span>
        </span>
        <Selo tom={plano.ativo ? 'sucesso' : 'neutro'}>{plano.ativo ? 'Ativo' : 'Inativo'}</Selo>
      </span>
      <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        {plano.preco_mensal_centavos ? (
          <>
            <span className="text-[26px] font-bold tabular-nums tracking-tight text-text-title">
              {formatCurrency(plano.preco_mensal_centavos / 100)}
            </span>
            <span className="text-[13px] text-text-secondary">/mês</span>
          </>
        ) : (
          <span className="text-[22px] font-bold text-text-title">Sob consulta</span>
        )}
        {plano.preco_anual_centavos ? (
          <span className="text-[13px] tabular-nums text-text-hint">
            · {formatCurrency(plano.preco_anual_centavos / 100)}/ano
          </span>
        ) : null}
      </span>
      <span className="grid w-full grid-cols-3 rounded-xl border border-border/70 bg-muted/50">
        {limites.map((l, i) => (
          <span
            key={l.rotulo}
            className={cn(
              'flex min-w-0 flex-col gap-0.5 px-3 py-2.5',
              i < limites.length - 1 && 'border-r border-border/70'
            )}
          >
            <span className="truncate text-base font-bold tabular-nums text-text-title">
              {formatNumber(l.valor)}
            </span>
            <span className="truncate text-[11px] text-text-secondary">{l.rotulo}</span>
          </span>
        ))}
      </span>
    </button>
  );
}

const inputClass =
  'w-full rounded-lg border border-border bg-bg-elevated px-4 py-2 text-sm text-text-body placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';
const labelClass = 'mb-2 block text-sm font-medium text-text-title';

export function Plans() {
  const { data: plans, isLoading, error } = usePlans();
  const createPlanMutation = useCreatePlan();
  const updatePlanMutation = useUpdatePlan();

  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plano | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const isSaving = createPlanMutation.isPending || updatePlanMutation.isPending;

  const openCreate = () => {
    setEditingPlan(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (plano: Plano) => {
    setEditingPlan(plano);
    setForm(planToForm(plano));
    setShowModal(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setShowModal(false);
    setEditingPlan(null);
  };

  const handleSave = async () => {
    if (!form.codigo.trim() || !form.nome.trim()) {
      alert('Código e nome são obrigatórios');
      return;
    }
    try {
      const payload = formToInput(form);
      if (editingPlan) {
        await updatePlanMutation.mutateAsync({ id: editingPlan.id, data: payload });
      } else {
        await createPlanMutation.mutateAsync(payload);
      }
      setShowModal(false);
      setEditingPlan(null);
    } catch (err) {
      console.error('Erro ao salvar plano:', err);
      alert(
        'Erro ao salvar o plano. Verifique os dados (código pode já existir) e tente novamente.'
      );
    }
  };

  const list = plans ?? [];

  if (error) {
    return (
      <Layout breadcrumbs={[{ label: 'Admin' }, { label: 'Planos' }]}>
        <div className="flex flex-col gap-4 p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-center h-96">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-error/10">
                <AlertTriangle size={26} className="text-error" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-text-title">
                Erro ao carregar planos
              </h2>
              <p className="text-sm text-text-secondary">
                Não foi possível carregar o catálogo de planos.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: 'Admin' }, { label: 'Planos' }]}>
      <div className="flex flex-col gap-4 lg:gap-6 p-3 sm:p-4 lg:p-6">
        {/* Heading */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="max-md:hidden text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
              Planos
            </h1>
            <p className="mt-1 text-sm text-text-secondary">
              Catálogo de assinatura — limites, escopo e preços de exibição.
            </p>
          </div>
          <Button onClick={openCreate} className="max-md:hidden font-semibold">
            <Plus size={18} />
            <span>Novo plano</span>
          </Button>
          <BotaoFlutuante icon={Plus} label="Novo plano" onClick={openCreate} />
        </div>

        {/* Tabela (desktop) + cartões (celular/tablet). Com dados, o cartão de
            fora sai: a tabela tem o seu e cada plano no celular é um bloco. */}
        <div
          className={cn(
            'w-full animate-fade-up',
            (isLoading || list.length === 0) &&
              'overflow-hidden rounded-xl border border-border bg-card shadow-card'
          )}
        >
          {isLoading ? (
            <div className="overflow-x-auto">
              <TabelaCartoes className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                  <tr>
                    <th className="px-6 py-3">Plano</th>
                    <th className="px-6 py-3">Animais</th>
                    <th className="px-6 py-3">Func.</th>
                    <th className="px-6 py-3">Prop.</th>
                    <th className="px-6 py-3">Mensal</th>
                    <th className="px-6 py-3">Anual</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Ações</th>
                  </tr>
                </thead>
              </TabelaCartoes>
              <TableSkeleton
                rows={3}
                columns={['w-32', 'w-16', 'w-12', 'w-12', 'w-20', 'w-20', 'w-16', 'w-16']}
              />
            </div>
          ) : list.length > 0 ? (
            <>
              <ul className="flex flex-col gap-3 lg:hidden">
                {list.map((plano) => (
                  <li key={plano.id}>
                    <PlanoCard plano={plano} onEdit={() => openEdit(plano)} />
                  </li>
                ))}
              </ul>
              <div className="hidden overflow-hidden rounded-xl border border-border bg-card shadow-card lg:block">
                <div className="overflow-x-auto">
                  <TabelaCartoes className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                      <tr>
                        <th className="px-6 py-3">Plano</th>
                        <th className="px-6 py-3">Animais</th>
                        <th className="px-6 py-3">Func.</th>
                        <th className="px-6 py-3">Prop.</th>
                        <th className="px-6 py-3">Mensal</th>
                        <th className="px-6 py-3">Anual</th>
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {list.map((plano) => (
                        <tr key={plano.id} className="hover:bg-muted/30">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-text-title">{plano.nome}</span>
                              {plano.popular && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-tertiary/10 px-2 py-0.5 text-[11px] font-medium text-tertiary">
                                  <Star size={11} className="fill-tertiary text-tertiary" />
                                  Popular
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-text-hint">{plano.codigo}</span>
                          </td>
                          <td className="px-6 py-4 tabular-nums text-text-body">
                            {plano.limite_animais}
                          </td>
                          <td className="px-6 py-4 tabular-nums text-text-body">
                            {plano.limite_funcionarios}
                          </td>
                          <td className="px-6 py-4 tabular-nums text-text-body">
                            {plano.limite_propriedades}
                          </td>
                          <td className="px-6 py-4 tabular-nums">
                            {plano.preco_mensal_centavos ? (
                              <span className="font-medium text-text-title">
                                {formatCurrency(plano.preco_mensal_centavos / 100)}
                              </span>
                            ) : (
                              <span className="text-text-hint">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 tabular-nums">
                            {plano.preco_anual_centavos ? (
                              <span className="font-medium text-text-title">
                                {formatCurrency(plano.preco_anual_centavos / 100)}
                              </span>
                            ) : (
                              <span className="text-text-hint">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                plano.ativo
                                  ? 'bg-online/10 text-online'
                                  : 'bg-muted text-text-secondary'
                              }`}
                            >
                              {plano.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openEdit(plano)}
                              className="text-xs"
                            >
                              <Pencil size={14} />
                              Editar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </TabelaCartoes>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10">
                  <Layers size={26} className="text-primary" />
                </div>
                <p className="mb-1 text-lg font-medium text-text-title">Nenhum plano cadastrado</p>
                <p className="text-sm text-text-secondary">Crie o primeiro plano do catálogo.</p>
              </div>
            </div>
          )}
        </div>

        {/* Create / Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm max-md:items-end max-md:p-0">
            <div className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-elevated max-h-[90vh] overflow-y-auto animate-fade-up max-md:max-h-[92dvh] max-md:rounded-b-none max-md:border-x-0 max-md:border-b-0 max-md:pb-[env(safe-area-inset-bottom)]">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card p-6">
                <h2 className="text-2xl font-semibold text-text-title">
                  {editingPlan ? 'Editar Plano' : 'Novo Plano'}
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeModal}
                  disabled={isSaving}
                  aria-label="Fechar"
                  className="rounded-full"
                >
                  <X size={20} />
                </Button>
              </div>

              <div className="p-6">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                  }}
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>Código *</label>
                      <input
                        type="text"
                        value={form.codigo}
                        onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                        className={inputClass}
                        placeholder="ex: basico"
                        required
                        disabled={!!editingPlan}
                      />
                      {editingPlan && (
                        <p className="mt-1 text-xs text-text-hint">
                          O código não pode ser alterado.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>Nome *</label>
                      <input
                        type="text"
                        value={form.nome}
                        onChange={(e) => setForm({ ...form, nome: e.target.value })}
                        className={inputClass}
                        placeholder="ex: Básico"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Descrição / Escopo</label>
                    <textarea
                      value={form.descricao_escopo}
                      onChange={(e) => setForm({ ...form, descricao_escopo: e.target.value })}
                      className={inputClass}
                      rows={2}
                      placeholder="Resumo do que o plano oferece"
                    />
                  </div>

                  {/* Limites */}
                  <div className="border-t border-border pt-4">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-secondary">
                      Limites
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className={labelClass}>Animais</label>
                        <input
                          type="number"
                          min={0}
                          value={form.limite_animais || ''}
                          onChange={(e) =>
                            setForm({ ...form, limite_animais: parseInt(e.target.value) || 0 })
                          }
                          className={inputClass}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Funcionários</label>
                        <input
                          type="number"
                          min={0}
                          value={form.limite_funcionarios || ''}
                          onChange={(e) =>
                            setForm({ ...form, limite_funcionarios: parseInt(e.target.value) || 0 })
                          }
                          className={inputClass}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Propriedades</label>
                        <input
                          type="number"
                          min={0}
                          value={form.limite_propriedades || ''}
                          onChange={(e) =>
                            setForm({ ...form, limite_propriedades: parseInt(e.target.value) || 0 })
                          }
                          className={inputClass}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preços de exibição */}
                  <div className="border-t border-border pt-4">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-secondary">
                      Preço de exibição (centavos)
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={labelClass}>Mensal (centavos)</label>
                        <input
                          type="number"
                          min={0}
                          value={form.preco_mensal_centavos || ''}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              preco_mensal_centavos: parseInt(e.target.value) || 0,
                            })
                          }
                          className={inputClass}
                          placeholder="ex: 4990"
                        />
                        <p className="mt-1 text-xs text-text-hint">
                          {form.preco_mensal_centavos
                            ? formatCurrency(form.preco_mensal_centavos / 100)
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <label className={labelClass}>Anual (centavos)</label>
                        <input
                          type="number"
                          min={0}
                          value={form.preco_anual_centavos || ''}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              preco_anual_centavos: parseInt(e.target.value) || 0,
                            })
                          }
                          className={inputClass}
                          placeholder="ex: 49900"
                        />
                        <p className="mt-1 text-xs text-text-hint">
                          {form.preco_anual_centavos
                            ? formatCurrency(form.preco_anual_centavos / 100)
                            : '—'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="border-t border-border pt-4">
                    <label className={labelClass}>Features (uma por linha)</label>
                    <textarea
                      value={form.features}
                      onChange={(e) => setForm({ ...form, features: e.target.value })}
                      className={inputClass}
                      rows={4}
                      placeholder={'Gestão de animais\nReprodução\nRelatórios'}
                    />
                  </div>

                  {/* Flags */}
                  <div className="flex flex-wrap items-center gap-6 border-t border-border pt-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-text-title">
                      <input
                        type="checkbox"
                        checked={form.ativo}
                        onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                        className="size-4 rounded border-border-strong text-primary focus:ring-primary/40"
                      />
                      Ativo
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-text-title">
                      <input
                        type="checkbox"
                        checked={form.popular}
                        onChange={(e) => setForm({ ...form, popular: e.target.checked })}
                        className="size-4 rounded border-border-strong text-primary focus:ring-primary/40"
                      />
                      Popular (destaque)
                    </label>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-text-title">Ordem</label>
                      <input
                        type="number"
                        min={0}
                        value={form.ordem || ''}
                        onChange={(e) => setForm({ ...form, ordem: parseInt(e.target.value) || 0 })}
                        className="w-20 rounded-lg border border-border bg-bg-elevated px-3 py-1.5 text-sm text-text-body transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-card p-6">
                <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <RefreshCw size={16} className="animate-spin" />}
                  {isSaving ? 'Salvando...' : editingPlan ? 'Salvar Alterações' : 'Criar Plano'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
